import { Injectable, EventEmitter, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';

import { RoleModel, GrantModel, UserModel, GrantAction } from '../models';

import { Observable, BehaviorSubject, of } from 'rxjs';
import { debounceTime, filter, switchMap, mergeAll, mergeMap, groupBy, distinctUntilChanged, catchError } from 'rxjs/operators';

import * as _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class RoleService {
    rolesUpdated: EventEmitter<void> = new EventEmitter<void>();
    roleError: EventEmitter<string> = new EventEmitter<string>();
    public roles = new Array<RoleModel>();
    roles$ = new BehaviorSubject<RoleModel[]>(new Array<RoleModel>());

    constructor(private http: HttpClient, private snackbar: MatSnackBar) {
        this.saveRoles(this.roles$).subscribe(
            data => {/*console.log('SUCCESS: ', data)*/ this.rolesUpdated.emit(); },
            err => {/*console.log('ERROR: ', err)*/ this.error(err.message); },
            // () => console.log('complete')
        );
    }

    private error(message: string, load: boolean = true): void {
        this.snackbar.open('Role Error: ' + message, '', {
            duration: 5000,
        });
        if (load) {
            this.loadRoles();
        } else {
            this.rolesUpdated.emit();
        }
    }

    public rolesChanged(): void {
        // const value = this.roles.map(a => ({ ...a }));  // make deep copy!!!
        const value = _.cloneDeep(this.roles);
        this.roles$.next(value);
    }

    public saveRoles(roles: Observable<RoleModel[]>): Observable<RoleModel> {
        return roles.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            mergeAll(),
            groupBy((element: RoleModel) => element._id),
            // mergeMap(group => group.pipe(
            //     filter((value: Role) => value !== undefined && value.id !== undefined),
            //     distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            //     switchMap((u: Role) => this._saveRole(u)),
            //     catchError(err => of(new Role() ))
            // )),
            mergeMap(group => this.saveRole(group)),
            catchError(err => { this.error(err.message); return of(new RoleModel()); })
        );
    }

    public saveRole(role: Observable<RoleModel>): Observable<RoleModel> {
        return role.pipe(
            debounceTime(500),
            filter((value: RoleModel) => value !== undefined && value._id !== undefined && value._id > 0),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            switchMap((u: RoleModel) => this._saveRole(u)),
            catchError(err => { this.error(err.message); return of(new RoleModel()); })
        );
    }

    private _saveRole(categorie: RoleModel): Observable<RoleModel> {
        return this.http.put<RoleModel>(`/api/roles/${categorie._id}`, categorie).pipe(
            catchError(err => { this.error(err.message); return of(new RoleModel()); })
        );
    }

    can(user: UserModel, action: GrantAction, resource: string): boolean {
        for (const roleId of user._roleIds) {
            const role = this.roles.find((element) => {
                return (element._id === roleId);
            });
            if (role) {
                const grant = role.grants.find((element) => {
                    return (element.entityName === resource && element.entityId === undefined && element.can(action));
                });
                if (grant) {
                    return true;
                }
            }
        }
        return false;
    }

    getAll(): Observable<RoleModel[]> {
        return this.http.get<RoleModel[]>('/api/roles');
    }

    get(id: number): Observable<RoleModel> {
        const role = this.roles.find((element: RoleModel) => {
            return (element._id === id);
        });
        if (role) {
            return of(role);
        } else {
            return this.http.get<RoleModel>(`/api/roles/${id}`);
        }
    }

    getByName(name: string): RoleModel|undefined {
        const role = this.roles.find((element: RoleModel) => {
            return (element.name === name);
        });
        return role;
    }

    add(cat: RoleModel): void {
        this.http.post<RoleModel>(`/api/roles`, {role: cat}).subscribe(
            data => { this.loadRoles(); },
            err => { this.error(err.message, false); }
        );
    }

    delete(id: number): void {
        this.http.delete<void>(`/api/roles/${id}`).subscribe(
            data => { this.loadRoles(); },
            err => { this.error(err.message, false); }
        );
    }

    getRoles(): RoleModel[] {
        return this.roles;
    }

    getGrants(role: RoleModel): void {
        this.http.get<GrantModel[]>(`/api/roles/${role._id}/grants`).subscribe(
            data => {
                // this.loadRoles();
                role.grants = data;
            },
            err => { this.error(err.message, false); }
        );
        // return new Array<{}>();
    }

    loadRoles(): void {
        this.roles.length = 0;
        this.getAll()
            .subscribe(data => {
                this.roles = data;
                for (const role of this.roles) {
                    this.getGrants(role);
                }
                this.rolesUpdated.emit();
            });
    }
}
